'use client';

import { c as _c } from "react/compiler-runtime";
import { StayPictogram } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { formatStationDistanceMeters } from '~/components/TripOverview/accommodationPage/formatStationDistance';
import { sanitzeBoardBasis } from '~/utils/accommodationHelpers';
import { sanitizeText } from '~/utils/sanitizeText';
import styles from './AccommodationCard.module.css';
import type { AccommodationCardProps } from './AccommodationCard.types';
import { AccommodationCardNightStepper } from './AccommodationCardNightStepper';
import { AccommodationCardSleeperContent } from './AccommodationCardSleeperContent';
import { AccommodationCardStayContent } from './AccommodationCardStayContent';
import { ItineraryCard } from './ItineraryCard';
import type { TagItem } from './ItineraryCard.types';
function getBodyText(body: AccommodationCardProps['body']): string {
  const textCard = body.find(card => card.type === 'text');
  return textCard && 'text' in textCard ? sanitizeText(String(textCard.text)) : '';
}
function getAccommodationDetails(body: AccommodationCardProps['body']) {
  const accCard = body.find(card => card.type === 'accommodation');
  if (!accCard || !('defaultAccommodation' in accCard) || !accCard.defaultAccommodation) return null;
  return accCard.defaultAccommodation as {
    accommodation: {
      title: string;
      roomName: string;
      boardBasis?: string;
    };
  };
}
export const AccommodationCard = t0 => {
  const $ = _c(78);
  const {
    title,
    nights,
    images,
    body,
    section,
    stayItemId,
    tripId,
    accommodationStay,
    accommodationVariant: t1,
    sleeperDetails,
    accommodationIncluded
  } = t0;
  const accommodationVariant = t1 === undefined ? "stay-expanded" : t1;
  const t = useTranslations("journey");
  let t2;
  if ($[0] !== body) {
    t2 = getBodyText(body);
    $[0] = body;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const bodyText = t2;
  let accommodationDetails;
  let accommodationTitle;
  let arrivalDistance;
  let departureDistance;
  let overviewTags;
  let rooms;
  let t3;
  if ($[2] !== accommodationStay?.accommodation.distanceFromArrivalStationMeters || $[3] !== accommodationStay?.accommodation.distanceFromDepartureStationMeters || $[4] !== accommodationStay?.accommodation.roomName || $[5] !== accommodationStay?.accommodation.selectedRooms || $[6] !== accommodationStay?.accommodation.title || $[7] !== body || $[8] !== t) {
    accommodationDetails = getAccommodationDetails(body);
    accommodationTitle = accommodationStay?.accommodation.title ?? accommodationDetails?.accommodation.title;
    const selectedRooms = accommodationStay?.accommodation.selectedRooms ?? [];
    const fallbackRoomName = accommodationStay?.accommodation.roomName ?? accommodationDetails?.accommodation.roomName;
    const fallbackRooms = fallbackRoomName ? [{
      id: "fallback-room",
      displayName: fallbackRoomName
    }] : [];
    rooms = selectedRooms.length > 0 ? selectedRooms.map(_temp) : fallbackRooms;
    let t4;
    if ($[16] !== t) {
      t4 = t("overview_accommodation_included");
      $[16] = t;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <StayPictogram size="small" decorative={true} />;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== t4) {
      t6 = [{
        label: t4,
        variant: "positive",
        icon: t5
      }];
      $[19] = t4;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    overviewTags = t6;
    const arrivalMeters = accommodationStay?.accommodation.distanceFromArrivalStationMeters;
    const departureMeters = accommodationStay?.accommodation.distanceFromDepartureStationMeters;
    let t7;
    if ($[21] !== arrivalMeters || $[22] !== t) {
      t7 = arrivalMeters != null && arrivalMeters > 0 ? t("accommodation_distance_arrival", {
        distance: formatStationDistanceMeters(arrivalMeters)
      }) : null;
      $[21] = arrivalMeters;
      $[22] = t;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    arrivalDistance = t7;
    let t8;
    if ($[24] !== departureMeters || $[25] !== t) {
      t8 = departureMeters != null && departureMeters > 0 ? t("accommodation_distance_departure", {
        distance: formatStationDistanceMeters(departureMeters)
      }) : null;
      $[24] = departureMeters;
      $[25] = t;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    departureDistance = t8;
    const selectedBoardBases = selectedRooms.map(_temp2);
    t3 = new Set((selectedBoardBases.length > 0 ? selectedBoardBases : [accommodationDetails?.accommodation.boardBasis ?? null]).map(sanitzeBoardBasis).filter(Boolean));
    $[2] = accommodationStay?.accommodation.distanceFromArrivalStationMeters;
    $[3] = accommodationStay?.accommodation.distanceFromDepartureStationMeters;
    $[4] = accommodationStay?.accommodation.roomName;
    $[5] = accommodationStay?.accommodation.selectedRooms;
    $[6] = accommodationStay?.accommodation.title;
    $[7] = body;
    $[8] = t;
    $[9] = accommodationDetails;
    $[10] = accommodationTitle;
    $[11] = arrivalDistance;
    $[12] = departureDistance;
    $[13] = overviewTags;
    $[14] = rooms;
    $[15] = t3;
  } else {
    accommodationDetails = $[9];
    accommodationTitle = $[10];
    arrivalDistance = $[11];
    departureDistance = $[12];
    overviewTags = $[13];
    rooms = $[14];
    t3 = $[15];
  }
  let t4;
  if ($[27] !== t3) {
    t4 = [...t3];
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  const boardBases = t4;
  if (section === "accommodation") {
    if (accommodationVariant === "sleeper" && sleeperDetails) {
      let t5;
      if ($[29] !== images || $[30] !== sleeperDetails) {
        t5 = <AccommodationCardSleeperContent images={images} sleeperDetails={sleeperDetails} />;
        $[29] = images;
        $[30] = sleeperDetails;
        $[31] = t5;
      } else {
        t5 = $[31];
      }
      return t5;
    }
    let t5;
    if ($[32] !== accommodationDetails || $[33] !== accommodationStay || $[34] !== accommodationTitle || $[35] !== accommodationVariant || $[36] !== arrivalDistance || $[37] !== boardBases || $[38] !== bodyText || $[39] !== departureDistance || $[40] !== images || $[41] !== rooms || $[42] !== stayItemId || $[43] !== title || $[44] !== tripId) {
      t5 = <AccommodationCardStayContent placeTitle={title} images={images} variant={accommodationVariant} accommodationTitle={accommodationTitle} rooms={rooms} accommodationDetails={accommodationDetails} bodyText={bodyText} accommodationStay={accommodationStay} arrivalDistance={arrivalDistance} departureDistance={departureDistance} boardBases={boardBases} tripId={tripId} itemId={stayItemId} />;
      $[32] = accommodationDetails;
      $[33] = accommodationStay;
      $[34] = accommodationTitle;
      $[35] = accommodationVariant;
      $[36] = arrivalDistance;
      $[37] = boardBases;
      $[38] = bodyText;
      $[39] = departureDistance;
      $[40] = images;
      $[41] = rooms;
      $[42] = stayItemId;
      $[43] = title;
      $[44] = tripId;
      $[45] = t5;
    } else {
      t5 = $[45];
    }
    return t5;
  }
  let t5;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <StayPictogram size="small" decorative={true} />;
    $[46] = t5;
  } else {
    t5 = $[46];
  }
  let t6;
  if ($[47] !== t || $[48] !== title) {
    t6 = t("itinerary_stay_in", {
      place: title
    });
    $[47] = t;
    $[48] = title;
    $[49] = t6;
  } else {
    t6 = $[49];
  }
  let t7;
  if ($[50] !== t6) {
    t7 = <ItineraryCard.Header icon={t5} title={t6} />;
    $[50] = t6;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  let t8;
  if ($[52] !== images) {
    t8 = images.length > 0 ? <ItineraryCard.Gallery images={images} /> : null;
    $[52] = images;
    $[53] = t8;
  } else {
    t8 = $[53];
  }
  let t9;
  if ($[54] !== nights || $[55] !== stayItemId || $[56] !== tripId) {
    t9 = <AccommodationCardNightStepper nights={nights} tripId={tripId} stayItemId={stayItemId} />;
    $[54] = nights;
    $[55] = stayItemId;
    $[56] = tripId;
    $[57] = t9;
  } else {
    t9 = $[57];
  }
  let t10;
  if ($[58] !== accommodationIncluded || $[59] !== overviewTags) {
    t10 = accommodationIncluded ? <ItineraryCard.Tags tags={overviewTags} /> : null;
    $[58] = accommodationIncluded;
    $[59] = overviewTags;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  let t11;
  if ($[61] !== t) {
    t11 = t("overview_choose_accommodation");
    $[61] = t;
    $[62] = t11;
  } else {
    t11 = $[62];
  }
  let t12;
  if ($[63] !== t11) {
    t12 = <ItineraryCard.InfoBanner>{t11}</ItineraryCard.InfoBanner>;
    $[63] = t11;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] !== t10 || $[66] !== t12) {
    t13 = <div className={styles.instructionalGroup}>{t10}{t12}</div>;
    $[65] = t10;
    $[66] = t12;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  let t14;
  if ($[68] !== t13 || $[69] !== t9) {
    t14 = <div className={styles.overviewBlock}>{t9}{t13}</div>;
    $[68] = t13;
    $[69] = t9;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== bodyText) {
    t15 = bodyText ? <ItineraryCard.Body text={bodyText} /> : null;
    $[71] = bodyText;
    $[72] = t15;
  } else {
    t15 = $[72];
  }
  let t16;
  if ($[73] !== t14 || $[74] !== t15 || $[75] !== t7 || $[76] !== t8) {
    t16 = <ItineraryCard>{t7}{t8}{t14}{t15}</ItineraryCard>;
    $[73] = t14;
    $[74] = t15;
    $[75] = t7;
    $[76] = t8;
    $[77] = t16;
  } else {
    t16 = $[77];
  }
  return t16;
};
function _temp(room) {
  return {
    id: String(room.id),
    displayName: room.displayName
  };
}
function _temp2(room_0) {
  return room_0.boardBasis ?? null;
}